import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar
} from 'recharts';
import './ProjectionsView.css';

const MONTH_COLORS = {
  'billable': '#0088FE',
  'non-billable': '#00C49F',
  'shadow': '#FFBB28'
};

const ProjectionsView = ({ monthlyProjections, name }) => {
  const [projectionViewType, setProjectionViewType] = useState('trends');

  return (
    <div className="projections-view">
      {/* Projections Controls */}
      <div className="projections-controls">
        <h3>📈 Monthly Allocation Projections</h3>
        <div className={`${monthlyProjections.length>0?"projection-filters":"hidden-projections"}`}>
          <button 
            className={`projection-filter-btn trends ${projectionViewType === 'trends' ? 'active' : ''}`}
            onClick={() => setProjectionViewType('trends')}
          >
            📈 Resource Count Trends
          </button>
          <button 
            className={`projection-filter-btn breakdown ${projectionViewType === 'breakdown' ? 'active' : ''}`}
            onClick={() => setProjectionViewType('breakdown')}
          >
            📊 Detailed Breakdown
          </button>
          <button 
            className={`projection-filter-btn deviation ${projectionViewType === 'deviation' ? 'active' : ''}`}
            onClick={() => setProjectionViewType('deviation')}
          >
            📉 Deviation Analysis
          </button>
          <button 
            className={`projection-filter-btn comparison ${projectionViewType === 'comparison' ? 'active' : ''}`}
            onClick={() => setProjectionViewType('comparison')}
          >
            ⚖️ Allocation Analysis
          </button>
        </div>
      </div>

      {/* Monthly Allocation Trends */}
      {projectionViewType === 'trends' && (
        <div className="chart-section full-width">
          <h2>📈 Monthly Resource Counts for {name} (2025)</h2>
          {monthlyProjections.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={monthlyProjections} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis label={{ value: 'Resource Count', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="billableCount" 
                  stroke={MONTH_COLORS.billable} 
                  strokeWidth={3}
                  name="Billable"
                  dot={{ fill: MONTH_COLORS.billable, strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="nonBillableCount" 
                  stroke={MONTH_COLORS['non-billable']} 
                  strokeWidth={3}
                  name="Non-Billable"
                  dot={{ fill: MONTH_COLORS['non-billable'], strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="shadowCount" 
                  stroke={MONTH_COLORS.shadow} 
                  strokeWidth={3}
                  name="Shadow"
                  dot={{ fill: MONTH_COLORS.shadow, strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">
              <h3>📊 No projections data available</h3>
              {/* <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p> */}
            </div>
          )}
        </div>
      )}

      {/* Resource Count Breakdown */}
      {projectionViewType === 'breakdown' && (
        <div className="chart-section full-width">
          <h2>📊 Monthly Resource Count Breakdown (Detailed View)</h2>
          {monthlyProjections.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={monthlyProjections} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis label={{ value: 'Resource Count', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [value, `${name} Resources`]} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="billableCount" 
                  stroke={MONTH_COLORS.billable} 
                  strokeWidth={3}
                  name="Billable Count"
                  dot={{ fill: MONTH_COLORS.billable, strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="nonBillableCount" 
                  stroke={MONTH_COLORS['non-billable']} 
                  strokeWidth={3}
                  name="Non-Billable Count"
                  dot={{ fill: MONTH_COLORS['non-billable'], strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="shadowCount" 
                  stroke={MONTH_COLORS.shadow} 
                  strokeWidth={3}
                  name="Shadow Count"
                  dot={{ fill: MONTH_COLORS.shadow, strokeWidth: 2, r: 6 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">
              <h3>📊 No projections data available</h3>
              <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p>
            </div>
          )}
        </div>
      )}

      {/* Comparison View */}
      {projectionViewType === 'comparison' && (
        <>
          <div className="chart-section">
            <h2>⚖️ Resource Count vs Allocation Analysis</h2>
            {monthlyProjections.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyProjections} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis yAxisId="left" label={{ value: 'Resource Count', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Allocation (%)', angle: 90, position: 'insideRight' }} />
                  <Tooltip formatter={(value, name) => [
                    name.includes('Count') ? value : `${value.toFixed(1)}%`, 
                    name
                  ]} />
                  <Legend />
                  
                  {/* Resource Count Lines (Left Axis) */}
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="billableCount" 
                    stroke="#0088FE" 
                    strokeWidth={3}
                    name="Billable Count"
                    dot={{ fill: '#0088FE', strokeWidth: 2, r: 6 }}
                    connectNulls={false}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="nonBillableCount" 
                    stroke="#00C49F" 
                    strokeWidth={3}
                    name="Non-Billable Count"
                    dot={{ fill: '#00C49F', strokeWidth: 2, r: 6 }}
                    connectNulls={false}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="shadowCount" 
                    stroke="#FFBB28" 
                    strokeWidth={3}
                    name="Shadow Count"
                    dot={{ fill: '#FFBB28', strokeWidth: 2, r: 6 }}
                    connectNulls={false}
                  />
                  
                  {/* Allocation Lines (Right Axis) - Dashed */}
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="billableAllocation" 
                    stroke="#0088FE" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Billable Allocation %"
                    dot={{ fill: '#0088FE', strokeWidth: 1, r: 4 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">
                <h3>📊 No projections data available</h3>
                <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>

          {/* Monthly Summary Table */}
          <div className="chart-section">
            <h2>📋 Monthly Resource Summary (Python Data Structure)</h2>
            {monthlyProjections.length > 0 ? (
              <div className="projections-table">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Billable Count</th>
                      <th>Non-Billable Count</th>
                      <th>Shadow Count</th>
                      <th>Total Resources</th>
                      <th>Billable %</th>
                      <th>Non-Billable %</th>
                      <th>Shadow %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyProjections.map((month, index) => {
                      const totalResources = month.billableCount + month.nonBillableCount + month.shadowCount;
                      const billablePercent = totalResources > 0 ? ((month.billableCount / totalResources) * 100).toFixed(1) : 0;
                      const nonBillablePercent = totalResources > 0 ? ((month.nonBillableCount / totalResources) * 100).toFixed(1) : 0;
                      const shadowPercent = totalResources > 0 ? ((month.shadowCount / totalResources) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={index}>
                          <td className="month-name">{month.month}</td>
                          <td className="billable-count">{month.billableCount}</td>
                          <td className="nonbillable-count">{month.nonBillableCount}</td>
                          <td className="shadow-count">{month.shadowCount}</td>
                          <td className="total-resources">{totalResources}</td>
                          <td className="billable-allocation">{billablePercent}%</td>
                          <td className="nonbillable-allocation">{nonBillablePercent}%</td>
                          <td className="shadow-allocation">{shadowPercent}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <h3>📊 No projections data available</h3>
                <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Deviation Analysis View */}
      {projectionViewType === 'deviation' && (
        <>
          <div className="chart-section full-width">
            <h2>📉 Resource Deviation from Billable Baseline</h2>
            <p className="chart-description">Shows how Non-Billable and Shadow resources deviate from the Billable count baseline each month</p>
            {monthlyProjections.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={monthlyProjections} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis yAxisId="left" label={{ value: 'Resource Count (Baseline)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Deviation from Baseline', angle: 90, position: 'insideRight' }} />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name.includes('Deviation')) {
                        return [
                          value > 0 ? `+${value}` : value,
                          name
                        ];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  
                  {/* Baseline Bars (Billable Count) */}
                  <Bar 
                    yAxisId="left"
                    dataKey="billableCount" 
                    fill="#0088FE" 
                    name="Billable Baseline"
                    opacity={0.7}
                  />
                  
                  {/* Deviation Lines */}
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="nonBillableDeviation" 
                    stroke="#FF6B6B" 
                    strokeWidth={3}
                    name="Non-Billable Deviation"
                    dot={{ fill: '#FF6B6B', strokeWidth: 2, r: 6 }}
                    connectNulls={false}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="shadowDeviation" 
                    stroke="#FFB347" 
                    strokeWidth={3}
                    name="Shadow Deviation"
                    dot={{ fill: '#FFB347', strokeWidth: 2, r: 6 }}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">
                <h3>📊 No projections data available</h3>
                <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>

          {/* Deviation Analysis Table */}
          <div className="chart-section">
            <h2>📋 Monthly Deviation Analysis (Billable as Baseline)</h2>
            {monthlyProjections.length > 0 ? (
              <div className="projections-table">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Billable (Baseline)</th>
                      <th>Non-Billable Count</th>
                      <th>Non-Billable Deviation</th>
                      <th>Non-Billable Dev %</th>
                      <th>Shadow Count</th>
                      <th>Shadow Deviation</th>
                      <th>Shadow Dev %</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyProjections.map((month, index) => {
                      const nonBillableStatus = Math.abs(month.nonBillableDeviation) > month.billableCount * 0.5 ? 'critical' : 
                                              Math.abs(month.nonBillableDeviation) > month.billableCount * 0.2 ? 'warning' : 'normal';
                      const shadowStatus = Math.abs(month.shadowDeviation) > month.billableCount * 0.5 ? 'critical' : 
                                          Math.abs(month.shadowDeviation) > month.billableCount * 0.2 ? 'warning' : 'normal';
                      const overallStatus = [nonBillableStatus, shadowStatus].includes('critical') ? 'critical' : 
                                           [nonBillableStatus, shadowStatus].includes('warning') ? 'warning' : 'normal';
                      
                      return (
                        <tr key={index} className={`deviation-row ${overallStatus}`}>
                          <td className="month-name">{month.month}</td>
                          <td className="billable-baseline"><strong>{month.billableCount}</strong></td>
                          <td className="nonbillable-count">{month.nonBillableCount}</td>
                          <td className={`deviation-value ${month.nonBillableDeviation >= 0 ? 'positive' : 'negative'}`}>
                            {month.nonBillableDeviation > 0 ? '+' : ''}{month.nonBillableDeviation}
                          </td>
                          <td className={`deviation-percent ${month.nonBillableDeviationPercent >= 0 ? 'positive' : 'negative'}`}>
                            {month.nonBillableDeviationPercent > 0 ? '+' : ''}{month.nonBillableDeviationPercent}%
                          </td>
                          <td className="shadow-count">{month.shadowCount}</td>
                          <td className={`deviation-value ${month.shadowDeviation >= 0 ? 'positive' : 'negative'}`}>
                            {month.shadowDeviation > 0 ? '+' : ''}{month.shadowDeviation}
                          </td>
                          <td className={`deviation-percent ${month.shadowDeviationPercent >= 0 ? 'positive' : 'negative'}`}>
                            {month.shadowDeviationPercent > 0 ? '+' : ''}{month.shadowDeviationPercent}%
                          </td>
                          <td className={`status-badge ${overallStatus}`}>
                            {overallStatus === 'critical' ? '🔴 Critical' : 
                             overallStatus === 'warning' ? '🟡 Warning' : '🟢 Normal'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Deviation Summary */}
                <div className="deviation-summary">
                  <h3>📊 Deviation Summary</h3>
                  <div className="summary-cards">
                    <div className="summary-card">
                      <h4>📉 Largest Non-Billable Deviation</h4>
                      <p>{Math.max(...monthlyProjections.map(m => Math.abs(m.nonBillableDeviation)))} resources</p>
                    </div>
                    <div className="summary-card">
                      <h4>📉 Largest Shadow Deviation</h4>
                      <p>{Math.max(...monthlyProjections.map(m => Math.abs(m.shadowDeviation)))} resources</p>
                    </div>
                    <div className="summary-card">
                      <h4>📊 Average Billable Baseline</h4>
                      <p>{(monthlyProjections.reduce((sum, m) => sum + m.billableCount, 0) / monthlyProjections.length).toFixed(1)} resources</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <h3>📊 No projections data available</h3>
                <p>Please ensure the allocation-projections.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectionsView; 